import { Component, AfterViewInit, ViewEncapsulation, PLATFORM_ID, Inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { isPlatformBrowser } from '@angular/common'

declare let hbspt: any

@Component({
  selector: 'flowaccount-partner-flowaccount-form',
  templateUrl: './partner-flowaccount-form.component.html',
  styleUrls: ['./partner-flowaccount-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PartnerFlowaccountFormComponent implements AfterViewInit {
  constructor(private translate: TranslateService, @Inject(PLATFORM_ID) private platformId: object) {}

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.loadHubspotScript()
    }
  }

  private loadHubspotScript(): void {
    const scriptId = 'hubspot-script'
    const alreadyLoaded = sessionStorage.getItem('hubspot-script-loaded') === 'true'
    const existingScript = document.getElementById(scriptId)

    if (alreadyLoaded && existingScript) {
      this.initHubspotForm()
      return
    }

    const script = document.createElement('script')
    script.id = scriptId
    script.src = 'https://js.hsforms.net/forms/embed/v2.js'
    script.async = true
    script.onload = () => {
      sessionStorage.setItem('hubspot-script-loaded', 'true')
      this.initHubspotForm()
    }
    document.body.appendChild(script)
  }

  private initHubspotForm(): void {
    if (typeof window !== 'undefined' && typeof window['hbspt']?.forms?.create === 'function') {
      window['hbspt'].forms.create({
        portalId: '20965110',
        formId: 'fcbe293b-2735-42db-b3df-961c3b3428f8',
        target: '#hubspot-form',
        region: 'na1',
        css: '',
        onFormReady: (form: HTMLElement) => {
          if (!form) return
          this.customizePlaceholders(form)
          this.customizeLabels(form)
          this.convertRadioToDropdown(form, {
            fieldName: '2-5151493/customer_buiness_category',
            placeholderKey: 'PARTNER_FORM.PLACEHOLDER.BUSINESS_TYPE',
          })
          this.convertRadioToDropdown(form, {
            fieldName: '2-5151493/no_of_accounting_software',
            placeholderKey: 'PARTNER_FORM.PLACEHOLDER.ACC_SOFTWARE',
          })
          this.modifySubmitButton(form)
          this.appendAgreementLink(form)
          this.applyManualErrorClass(form)
          this.updateSelectPlaceholderStyle(form)
          this.applyNumberValidation(form)
        },

        onFormSubmitted: () => {
          const observer = new MutationObserver(() => {
            const thankYouEl = document.querySelector('.submitted-message')
            if (thankYouEl && !thankYouEl.classList.contains('fa-thankyou-updated')) {
              this.translate.get('PARTNER_FORM.THANK_YOU_MESSAGE').subscribe((res) => {
                thankYouEl.textContent = res
                thankYouEl.classList.add('flow-form-thankyou-patched')
              })

              const target = document.getElementById('partnerFlowaccountForm-id')
              if (target) {
                const offset = window.innerWidth < 1170 ? 100 : 130
                const top = target.getBoundingClientRect().top + window.pageYOffset - offset

                window.scrollTo({
                  top,
                  behavior: 'smooth',
                })
              }

              observer.disconnect()
            }
          })

          observer.observe(document.body, { childList: true, subtree: true })
        },
      })
    }
  }

  private convertRadioToDropdown(
    form: HTMLElement,
    options: {
      fieldName: string
      placeholderKey: string
    },
  ) {
    const wrapper = form.querySelector(`.hs_${options.fieldName.replace(/\//g, '\\2f ')}`)
    if (!wrapper) return

    const radioInputs = wrapper.querySelectorAll<HTMLInputElement>('input[type="radio"]')
    const labels: string[] = []

    radioInputs.forEach((input) => {
      const label = input.closest('label')?.innerText.trim()
      if (label) labels.push(label)
    })

    const select = document.createElement('select')
    select.className = 'hs-input'
    select.name = options.fieldName
    select.required = true

    const defaultOption = document.createElement('option')
    defaultOption.value = ''
    defaultOption.disabled = true
    defaultOption.selected = true

    this.translate.get(options.placeholderKey).subscribe((res) => {
      defaultOption.text = res
    })

    select.appendChild(defaultOption)

    const radioList = wrapper.querySelector('ul.inputs-list') as HTMLElement
    if (radioList) radioList.style.display = 'none'

    labels.forEach((label) => {
      const option = document.createElement('option')
      option.value = label
      option.text = label
      select.appendChild(option)
    })

    const inputContainer = wrapper.querySelector('.input')
    if (inputContainer) {
      inputContainer.appendChild(select)

      select.addEventListener('change', () => {
        const value = select.value
        const matchingRadio = Array.from(radioInputs).find(
          (input) => input.closest('label')?.innerText.trim() === value,
        )
        if (matchingRadio) {
          matchingRadio.checked = true
          matchingRadio.required = true
          matchingRadio.dispatchEvent(new Event('input', { bubbles: true }))
          matchingRadio.dispatchEvent(new Event('change', { bubbles: true }))
        }
        if (select.classList.contains('error') && value !== '') {
          select.classList.remove('error')
        }
        select.classList.remove('is-placeholder')
      })
    }
  }

  private customizePlaceholders(form: HTMLElement) {
    const fields = [
      { name: 'firstname', key: 'PARTNER_FORM.PLACEHOLDER.FIRSTNAME' },
      { name: 'lastname', key: 'PARTNER_FORM.PLACEHOLDER.LASTNAME' },
      { name: 'email', key: 'PARTNER_FORM.PLACEHOLDER.EMAIL' },
      { name: '2-5151493/partner_name', key: 'PARTNER_FORM.PLACEHOLDER.FIRM_NAME' },
      { name: '2-5151493/total_customers', key: 'PARTNER_FORM.PLACEHOLDER.CUSTOMER_TOTAL' },
      { name: '2-5151493/existing_customers_in_accfirm', key: 'PARTNER_FORM.PLACEHOLDER.CUSTOMER_FLOW' },
      { name: '2-5151493/no_of_accounting_software', key: 'PARTNER_FORM.PLACEHOLDER.ACC_SOFTWARE' },
      { name: 'province', key: 'PARTNER_FORM.PLACEHOLDER.PROVINCE' },
    ]

    fields.forEach(({ name, key }) => {
      const input = form?.querySelector<HTMLInputElement>(`input[name="${name}"]`)
      if (input) this.translate.get(key).subscribe((res) => (input.placeholder = res))
    })

    const phoneInput = form?.querySelector<HTMLInputElement>('input[type="tel"]')
    if (phoneInput)
      this.translate.get('PARTNER_FORM.PLACEHOLDER.PHONE').subscribe((res) => (phoneInput.placeholder = res))

    const selects = [
      { name: '2-5151493/partner_business_type', key: 'PARTNER_FORM.PLACEHOLDER.POSITION' },
      { name: '2-5151493/customer_buiness_category', key: 'PARTNER_FORM.PLACEHOLDER.BUSINESS_TYPE' },
    ]

    selects.forEach(({ name, key }) => {
      const select = form?.querySelector<HTMLSelectElement>(`select[name="${name}"]`)
      if (select) this.translate.get(key).subscribe((res) => (select.options[0].text = res))
    })
  }

  private customizeLabels(form: HTMLElement) {
    const labels = [
      { name: 'firstname', key: 'PARTNER_FORM.FIELDS.FIRSTNAME' },
      { name: 'lastname', key: 'PARTNER_FORM.FIELDS.LASTNAME' },
      { name: '2-5151493/partner_name', key: 'PARTNER_FORM.FIELDS.FIRM_NAME' },
      { name: 'phone', key: 'PARTNER_FORM.FIELDS.PHONE' },
      { name: 'email', key: 'PARTNER_FORM.FIELDS.EMAIL' },
      { name: '2-5151493/partner_business_type', key: 'PARTNER_FORM.FIELDS.POSITION' },
      { name: '2-5151493/total_customers', key: 'PARTNER_FORM.FIELDS.CUSTOMER_TOTAL' },
      { name: '2-5151493/existing_customers_in_accfirm', key: 'PARTNER_FORM.FIELDS.CUSTOMER_FLOW' },
      { name: '2-5151493/customer_buiness_category', key: 'PARTNER_FORM.FIELDS.BUSINESS_TYPE' },
      { name: '2-5151493/no_of_accounting_software', key: 'PARTNER_FORM.FIELDS.ACC_SOFTWARE' },
      { name: 'province', key: 'PARTNER_FORM.FIELDS.PROVINCE' },
    ]

    labels.forEach(({ name, key }) => {
      const labelEl =
        form.querySelector(`label[for*="${name}"] > span:first-child`) ??
        form.querySelector(`label[id*="${name}"] > span:first-child`)
      if (labelEl) {
        this.translate.get(key).subscribe((res) => {
          labelEl.textContent = res + ':'
        })
      }
    })
  }

  private modifySubmitButton(form: HTMLElement) {
    const submitBtn = form.querySelector<HTMLInputElement>('input[type="submit"]')
    if (submitBtn) this.translate.get('PARTNER_FORM.SUBMIT').subscribe((res) => (submitBtn.value = res))
  }

  private appendAgreementLink(form: HTMLElement) {
    const label = form.querySelector('.hs-form-booleancheckbox-display')
    if (!label || label.querySelector('.agreement-inline')) return

    const oldSpan = label.querySelector('span')
    if (oldSpan) label.removeChild(oldSpan)

    const wrapper = document.createElement('div')
    wrapper.className = 'agreement-inline'

    const left = document.createElement('span')
    const link = document.createElement('a')
    const icon = document.createElement('i')

    icon.className = 'fas fa-angle-right'
    link.href = 'https://form.flowaccount.com/detail-terms-and-condition'
    link.target = '_blank'

    this.translate.get('PARTNER_FORM.AGREEMENT.CHECKBOX').subscribe((res) => (left.textContent = res))
    this.translate.get('PARTNER_FORM.AGREEMENT.LINK').subscribe((res) => (link.textContent = res))

    link.appendChild(icon)
    wrapper.appendChild(left)
    wrapper.appendChild(link)
    label.appendChild(wrapper)
  }
  private applyManualErrorClass(form: HTMLElement) {
    const requiredFields = Array.from(
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input[required], select[required]'),
    )

    requiredFields.forEach((field) => {
      field.addEventListener('blur', () => {
        if (field.value.trim() !== '') {
          field.classList.remove('error')
        }
      })
    })

    form.addEventListener('submit', () => {
      requiredFields.forEach((field) => {
        const value = field.value.trim()
        if (value === '') {
          field.classList.add('error')
        }
      })
    })
  }

  private updateSelectPlaceholderStyle(form: HTMLElement) {
    const selects = form.querySelectorAll<HTMLSelectElement>('select')

    selects.forEach((select) => {
      const updateColor = () => {
        if (select.value === '') {
          select.classList.add('is-placeholder')
        } else {
          select.classList.remove('is-placeholder')
        }
      }

      updateColor()
      select.addEventListener('change', updateColor)
    })
  }

  private applyNumberValidation(form: HTMLElement) {
    const numberFields = ['2-5151493/total_customers', '2-5151493/existing_customers_in_accfirm']

    numberFields.forEach((name) => {
      const input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`)
      if (input) {
        input.type = 'number'
        input.inputMode = 'numeric'
        input.pattern = '\\d*'
        input.min = '0'

        input.addEventListener('input', () => {
          input.value = input.value.replace(/^0+(?=\d)/, '').replace(/\D/g, '')
        })

        input.addEventListener('paste', (event: ClipboardEvent) => {
          const pasted = event.clipboardData?.getData('text') ?? ''
          if (/\D/.test(pasted)) {
            event.preventDefault()
          }
        })
      }
    })
  }
}
